// @flow
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/auth';
import SignProcess from '../model/SignProcess';
import SignProcessConfig from '../model/SignProcessConfig';
import { toSentence } from '../util/string';
import ApiResponse from '../model/ApiResponse';
import { loadGclid } from '../util/vendor';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '95%',
  },
  loader: {
    margin: '0 auto',
  },
  error: {
    color: theme.palette.error.main,
  },
}));

type Props = {
  config: SignProcessConfig,
  opened: boolean,
  onCreated: (?SignProcess) => void,
  provider: string,
};

const CreateSignRequest = (props: Props) => {
  const { doPost } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const { t } = useTranslation();
  const { config, opened, onCreated, provider } = props;

  const [open, setOpen] = useState(!!opened);
  const [processType, setProcessType] = useState(SignProcessConfig.signProcessTypes.NATURAL_PERSON_RUC);
  const [processTypeError, setProcessTypeError] = useState('');
  const [priceCode, setPriceCode] = useState(SignProcessConfig.signPriceCode.NATURAL_PERSON_RUC_2_YEARS);
  const [containerCode, setContainerCode] = useState(SignProcessConfig.signContainCode.NATURAL_PERSON_ARCHIVO);
  const [priceError, setPriceError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [gclid, setGclid] = useState('');

  useEffect(() => {
    const gclidValue = loadGclid();
    if (gclidValue) {
      setGclid(gclidValue);
    }
  }, []);

  const handleTypeChange = (event) => {
    const value = event.target.value || '';
    if (value !== processType) {
      setPriceCode('');
    }
    setProcessType(value);
  };

  const handlePriceChange = (event) => {
    setPriceCode(event.target.value || '');
  };

  const handleContainerChange = (event) => {
    setContainerCode(event.target.value || '');
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleOk = () => {
    if (processing) {
      return;
    }
    setProcessTypeError(!processType ? 'Required' : null);
    setPriceError(!priceCode ? 'Required' : null);
    if (processType && priceCode) {
      setProcessing(true);
      const data = {
        processType,
        priceCode,
        containerCode,
        provider,
        gclid,
      };
      doPost('/customer/sign-process', data)
        .then((response: ApiResponse) => {
          setProcessing(false);
          if (response.ok) {
            setOpen(false);
            onCreated(SignProcess.parse(response.data));
          } else if (!response.is401Error()) {
            const errorMessage = response.message || 'error.unknown';
            enqueueSnackbar(t(errorMessage, errorMessage), {
              variant: 'error',
            });
          }
        });
    }
  };

  const handleClose = () => {
    if (processing) {
      return;
    }
    setOpen(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleOpen}
      >
        {t('Request sign')}
      </Button>

      <Dialog
        disableEscapeKeyDown
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>
          {t('Please select person type')}
        </DialogTitle>
        <DialogContent>
          <form className={classes.container}>
            {processing && <CircularProgress className={classes.loader} size="1.5em" />}
            <FormControl className={classes.formControl} disabled={processing}>
              <InputLabel id="personType">
                {t('Person type')}
              </InputLabel>
              <Select
                labelId="personType"
                id="personTypeSelect"
                value={processType}
                onChange={handleTypeChange}
                input={<Input />}
              >
                {Object.keys(config.processTypes).map((item) => (
                  <MenuItem key={item} value={item}>
                    {t(toSentence(item))}
                  </MenuItem>
                ))}
              </Select>
              {processTypeError && <span className={classes.error}>{t(processTypeError)}</span>}
            </FormControl>
            {processType && <FormControl className={classes.formControl} disabled={processing}>
              <InputLabel id="priceLabel">
                {t('Sign duration')}
              </InputLabel>
              <Select
                labelId="priceLabel"
                id="priceSelect"
                value={priceCode}
                onChange={handlePriceChange}
                input={<Input />}
              >
                {config.processTypes[processType].map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.durationValue()} {t(item.durationLabel()).toLowerCase()}: ${item.price.toFixed(2)}
                  </MenuItem>
                ))}
              </Select>
              {priceError && <span className={classes.error}>{t(priceError)}</span>}
            </FormControl>}
            {processType && <FormControl className={classes.formControl} disabled={processing}>
              <InputLabel id="containerLabel">
                {t('Sign container')}
              </InputLabel>
              <Select
                labelId="containerLabel"
                id="containerSelect"
                value={containerCode}
                onChange={handleContainerChange}
                input={<Input />}
              >
                {(config.containers || []).map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    <b>{item.title}</b>&nbsp;<small>{item.description}</small>:&nbsp;${item.price.toFixed(2)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={processing}
            onClick={handleOk}
            variant="contained"
            color="secondary"
          >
            {t('OK')}
          </Button>
          <Button
            disabled={processing}
            onClick={handleClose}
            variant="contained"
            color="primary"
          >
            {t('Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateSignRequest;
